import React from 'react';
import { BarCustomLayerProps, BarDatum, ComputedBarDatum } from '@nivo/bar';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { z } from 'zod';
import { truncateTextIfNecessary } from 'libs/end-to-end/src/success-phenotype-v2-utils';

const POSSIBLE_UNITS = z.enum(['percentage', 'dollars', 'count']);
const PADDING = 19;

const getImpactYPost = (
  bar: ComputedBarDatum<BarDatum>,
  isPositive: boolean,
  isShort: boolean,
) => {
  if (!isShort) {
    const traitLevelImpactYPos = isPositive
      ? bar.y - 6
      : bar.y + bar.height - 6;

    return traitLevelImpactYPos;
  }

  return isPositive
    ? bar.y - PADDING * 2 - 12
    : bar.y + bar.height + PADDING * 2;
};

const getValueYPost = (
  bar: ComputedBarDatum<BarDatum>,
  isPositive: boolean,
  isShort: boolean,
) => {
  if (!isShort) {
    const traitLevelValueYPos = isPositive
      ? bar.y + bar.height - PADDING - 12
      : bar.y + PADDING;
    return traitLevelValueYPos;
  }

  return isPositive
    ? bar.y + bar.height - PADDING - 12
    : bar.y + bar.height + PADDING;
};

export const KeyFactorsLabelsLayer = (props: BarCustomLayerProps<BarDatum>) => {
  return (
    <g>
      {props.bars.flatMap((bar: ComputedBarDatum<BarDatum>) => {
        const isPositive = (bar.data.value ?? 0) >= 0;
        const isShort = bar.height < 42;
        const xCenter = bar.x + bar.width / 2;

        const units = String(bar.data.data[`${bar.data.id}_Units`]);
        const parsedUnits = POSSIBLE_UNITS.safeParse(units);

        if (!parsedUnits.success) {
          return null;
        }

        const traitLevelImpactLabel = formatNumberForDisplayInUnits({
          value: bar.data.value ?? 0,
          isPercentageAlreadyMultipliedBy100: true,
          units: parsedUnits.data,
        });

        const traitLevelValueLabel = bar.data.indexValue;

        const PIXEL_PER_CHART = 8;
        const levelValueMaxLength = Math.floor(
          (bar.width * 0.9) / PIXEL_PER_CHART,
        );

        return [
          <text
            key={`${bar.key}-label`}
            x={xCenter}
            y={getValueYPost(bar, isPositive, isShort)}
            textAnchor="middle"
            dominantBaseline="hanging"
            fill="black"
            fontSize={16}
            fontWeight="400"
          >
            {truncateTextIfNecessary({
              text: String(traitLevelValueLabel),
              maxChars: levelValueMaxLength,
            })}
            <title>{String(traitLevelValueLabel)}</title>
          </text>,
          <text
            key={`${bar.key}-value`}
            x={xCenter}
            y={getImpactYPost(bar, isPositive, isShort)}
            textAnchor="middle"
            dominantBaseline="hanging"
            fill="black"
            fontSize={16}
            fontWeight="400"
          >
            {truncateTextIfNecessary({
              text: String(traitLevelImpactLabel),
              maxChars: levelValueMaxLength,
            })}
            <title>{String(traitLevelImpactLabel)}</title>
          </text>,
        ];
      })}
    </g>
  );
};
